import { createTheme, alpha } from "@mui/material/styles";

const fontFamily =
  '"Montserrat", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export const colors = createTheme({
  palette: {
    primary: {
      main: "#07468E",
      dark: "#053a73",
    },
    secondary: {
      main: "#6C757D",
    },
    danger: {
      main: "#f25454",
      dark: "#d94343",
    },
    info: {
      main: "#31CDE8",
    },
    nav: {
      main: "#000922",
    },
    blue: {
      main: "#0B2D39",
    },
    white: {
      main: "#FFFFFF",
    },
    grey: {
      main: "#C7C7C7",
    },
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily,
    fontSize: 14,
    h1: { fontWeight: 700, letterSpacing: "-0.025em" },
    h2: { fontWeight: 700, letterSpacing: "-0.02em" },
    h3: { fontWeight: 600, letterSpacing: "-0.015em" },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600, letterSpacing: "-0.01em" },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 500 },
    subtitle2: { fontWeight: 600 },
    body1: { fontSize: "0.9375rem", lineHeight: 1.6 },
    body2: { fontSize: "0.875rem", lineHeight: 1.55 },
    button: {
      fontFamily,
      textTransform: "none",
      fontWeight: 600,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          border: "none",
          backgroundColor: "transparent",
          "& > .MuiButton-root": {
            margin: "16px 16px 8px",
            borderRadius: 8,
            textTransform: "none",
            fontWeight: 600,
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: "separate",
          borderSpacing: 0,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-root": {
            backgroundColor: "#fafbfc",
            color: "#374151",
            fontWeight: 600,
            fontSize: "0.8125rem",
            letterSpacing: 0,
            textTransform: "none",
            borderBottom: "1px solid #e5e7eb",
            paddingTop: 12,
            paddingBottom: 12,
            whiteSpace: "nowrap",
          },
          "& .MuiTableCell-stickyHeader": {
            backgroundColor: "#fafbfc",
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          "& .MuiTableRow-root:nth-of-type(even)": {
            backgroundColor: "transparent",
          },
          "& .MuiTableRow-root:hover": {
            backgroundColor: "#f9fafb !important",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderBottom: `1px solid ${alpha(theme.palette.nav?.main ?? "#000922", 0.07)}`,
          padding: "11px 16px",
          fontSize: "0.875rem",
          lineHeight: 1.45,
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: "background-color 0.15s ease",
          "&:last-child td": {
            borderBottom: 0,
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          color: theme.palette.text.secondary,
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
            {
              fontSize: "0.875rem",
            },
        }),
        toolbar: {
          paddingLeft: 16,
          paddingRight: 8,
          minHeight: 52,
        },
        actions: {
          "& .MuiIconButton-root": {
            borderRadius: 8,
          },
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: "inherit",
          "&:hover": {
            color: theme.palette.primary.main,
          },
          "&.Mui-active": {
            color: theme.palette.primary.main,
            fontWeight: 700,
          },
        }),
        icon: ({ theme }) => ({
          color: `${alpha(theme.palette.info.main, 0.95)} !important`,
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        contained: ({ theme }) => ({
          borderRadius: 10,
          textTransform: "none",
          fontWeight: 600,
          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
          "&:hover": {
            boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.26)}`,
          },
        }),
        outlined: {
          borderRadius: 10,
          textTransform: "none",
          fontWeight: 600,
        },
      },
    },
  },
});
